import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import cls from './account-list.module.scss'
import { SvgIcon } from '~src/new/shared/ui/SvgIcon/SvgIcon'
import { useApi } from '~shared/api/ApiContext'
import { useAppFrame } from '~shared/components/AppFrameContext'

/**
 * Перечень компаний для выбора (может быть использован в dropdown или в мобильном модальном листе).
 */
export const AccountList = ({ accounts = [], selectedAccountId, className, onClose }) => {
  const api = useApi()

  const accountsByGroup = useMemo(() => {
    const groups = {}
    for (const account of accounts) {
      const { groupName, _id } = account
      if (_id === selectedAccountId) continue
      if (!groups[groupName]) {
        groups[groupName] = []
      }
      groups[groupName].push(account)
    }
    return Object.values(groups)
  }, [accounts])

  const currentAccount = useMemo(() => {
    return accounts.find(account => account._id === selectedAccountId)
  }, [accounts, selectedAccountId])

  if (!accounts?.length) {
    return <div className={cls.empty}>Нет доступных точек</div>
  }

  const handleSelect = account => {
    const { request } = api.changeSelectedAccount({ selectedAccountId: account._id })
    request.finally(() => {
      onClose && onClose()
    })
  }

  return (
    <div className={classNames(cls.list, className)}>
      {currentAccount && <AccountItem account={currentAccount} />}
      <div className={cls.separator} />
      <span className={cls.separatorText}>Выберите точку</span>
      <div className={cls.groups}>
        {accountsByGroup.map(accounts => {
          return (
            <AccountGroup key={accounts[0].groupName} accounts={accounts} onSelect={handleSelect} />
          )
        })}
      </div>
    </div>
  )
}

const AccountGroup = ({ accounts, onSelect }) => {
  if (!accounts?.length) return null
  const { groupName } = accounts[0]
  return (
    <div className={cls.group}>
      <span className={cls.groupName}>{groupName}</span>
      {accounts.map(account => {
        const { _id } = account
        return <AccountItem key={_id} account={account} onSelect={onSelect} />
      })}
    </div>
  )
}

const AccountItem = ({ account, onSelect }) => {
  const { settings = {} } = useAppFrame()
  const { programs = {} } = settings

  const { _id, programTypes: [programType] = [] } = account

  const icon = programs[programType]?.whiteIcon

  return (
    <button
      key={_id}
      type="button"
      className={classNames(cls.item, { [cls.inactive]: !onSelect })}
      onClick={() => onSelect(account)}
    >
      <SvgIcon className={cls.programIcon} name={programType} icon={icon} />
      <AccountItemHeader account={account} />
    </button>
  )
}

export const AccountItemHeader = ({ account, isOnlyName = false, oneLineAddress = false }) => {
  if (!account) return null
  const { accountName, accountAddress, accountStatus } = account
  const isActive = accountStatus === 'active'
  return (
    <div className={cls.itemHeader}>
      {isOnlyName && <div className={cls.address}>Текущая точка</div>}
      <span className={cls.name}>
        {accountName} {!isActive && <span className={cls.archiveTag}>(Архив)</span>}
      </span>
      {accountAddress && !isOnlyName && (
        <p
          className={classNames(cls.address, {
            [cls.multilineAddress]: !oneLineAddress,
          })}
        >
          {accountAddress}
        </p>
      )}
    </div>
  )
}

AccountItem.propTypes = {
  account: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    address: PropTypes.string,
  }),
}
AccountList.propTypes = {
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      address: PropTypes.string,
    }),
  ),
  selectedAccountId: PropTypes.string,
  onSelect: PropTypes.func,
  className: PropTypes.string,
}
